import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const PRESETS = {
  Roster: { can_view_streaming: true, can_view_unsigned: false, can_view_scheduler: false, can_view_pgp: false, can_view_pgp_admin: false, can_view_operations: false, is_admin: false },
  Full: { can_view_streaming: true, can_view_unsigned: true, can_view_scheduler: false, can_view_pgp: false, can_view_pgp_admin: false, can_view_operations: false, is_admin: false },
  PGP: { can_view_streaming: false, can_view_unsigned: false, can_view_scheduler: false, can_view_pgp: true, can_view_pgp_admin: false, can_view_operations: false, is_admin: false },
  'PGP Admin': { can_view_streaming: false, can_view_unsigned: false, can_view_scheduler: false, can_view_pgp: true, can_view_pgp_admin: true, can_view_operations: false, is_admin: false },
  Operations: { can_view_streaming: false, can_view_unsigned: false, can_view_scheduler: false, can_view_pgp: false, can_view_pgp_admin: false, can_view_operations: true, is_admin: false },
  Admin: { can_view_streaming: true, can_view_unsigned: true, can_view_scheduler: true, can_view_pgp: true, can_view_pgp_admin: true, can_view_operations: true, can_manage_roster: true, is_admin: true },
};

const PERM_LABELS = [
  { key: 'is_active', label: 'Active' },
  { key: 'can_view_streaming', label: 'Streaming' },
  { key: 'can_view_unsigned', label: 'Unsigned' },
  { key: 'can_view_scheduler', label: 'Scheduler' },
  { key: 'can_view_pgp', label: 'PGP' },
  { key: 'can_view_pgp_admin', label: 'PGP Admin' },
  { key: 'can_view_operations', label: 'Operations' },
  { key: 'can_manage_roster', label: 'Roster Mgmt' },
  { key: 'is_admin', label: 'Admin' },
];

export default function AdminPage() {
  const qc = useQueryClient();
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePreset, setInvitePreset] = useState('Roster');
  const [inviteError, setInviteError] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const { data: users, isLoading, error } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => axios.get(`${API_URL}/api/admin/users`).then((r) => r.data),
  });

  const updateUser = useMutation({
    mutationFn: ({ id, patch }) => axios.patch(`${API_URL}/api/admin/users/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const deleteUser = useMutation({
    mutationFn: (id) => axios.delete(`${API_URL}/api/admin/users/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      setDeleteError('');
    },
    onError: (err) => {
      setDeleteError(err.response?.data?.error || 'Failed to delete user');
    },
  });

  const createUser = useMutation({
    mutationFn: (body) => axios.post(`${API_URL}/api/admin/users`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      setInviteEmail('');
      setInviteError('');
    },
    onError: (err) => {
      setInviteError(err.response?.data?.error || 'Failed to invite user');
    },
  });

  const handleInvite = (e) => {
    e.preventDefault();
    setInviteError('');
    if (!inviteEmail.includes('@')) {
      setInviteError('Enter a valid email');
      return;
    }
    createUser.mutate({ email: inviteEmail.trim().toLowerCase(), ...PRESETS[invitePreset] });
  };

  if (isLoading) return <div className="p-6">Loading users…</div>;
  if (error) return <div className="p-6 text-red-600">Error loading users: {String(error)}</div>;

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">User Management</h1>

      <form onSubmit={handleInvite} className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Invite user</h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="user@enc.llc"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
          <select
            value={invitePreset}
            onChange={(e) => setInvitePreset(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
          >
            {Object.keys(PRESETS).map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <button
            type="submit"
            disabled={createUser.isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {createUser.isLoading ? 'Inviting…' : 'Invite'}
          </button>
        </div>
        {inviteError && <div className="text-red-600 text-sm mt-2">{inviteError}</div>}
        <div className="text-xs text-gray-500 mt-2">
          Presets: <span className="font-medium">Roster</span> = streaming only ·{' '}
          <span className="font-medium">Full</span> = streaming + unsigned ·{' '}
          <span className="font-medium">PGP</span> = pgp.report only ·{' '}
          <span className="font-medium">Admin</span> = everything + manage users
        </div>
      </form>

      {deleteError && (
        <div className="mb-3 px-3 py-2 rounded border border-red-200 bg-red-50 text-red-700 text-sm">
          {deleteError}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-700 border-b">
              <th className="py-2 pr-4">Email</th>
              {PERM_LABELS.map((p) => (
                <th key={p.key} className="py-2 px-2 text-center">
                  {p.label}
                </th>
              ))}
              <th className="py-2 pl-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users?.map((u) => (
              <tr key={u.id} className="border-b last:border-0 hover:bg-gray-50">
                <td className="py-2 pr-4 font-medium text-gray-900">{u.email}</td>
                {PERM_LABELS.map((p) => (
                  <td key={p.key} className="py-2 px-2 text-center">
                    <input
                      type="checkbox"
                      checked={!!u[p.key]}
                      onChange={(e) =>
                        updateUser.mutate({ id: u.id, patch: { [p.key]: e.target.checked } })
                      }
                    />
                  </td>
                ))}
                <td className="py-2 pl-2 text-right">
                  {u.is_active && (
                    <button
                      onClick={() => {
                        if (window.confirm(
                          `Deactivate ${u.email}?\n\nThey lose access immediately. Songs, albums ` +
                          'and artists they tracked keep their name, and this can be undone by ' +
                          're-checking "Active".'
                        )) {
                          setDeleteError('');
                          deleteUser.mutate(u.id);
                        }
                      }}
                      className="text-red-600 hover:underline text-xs"
                    >
                      Deactivate
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
